import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { autoHyphen } from '@utils/common';
import { INIT_ADMIN_PW, DELETE_ADMIN } from '@graphql/mutations';
import { SEE_ADMIN_DETAIL } from '@graphql/queries';
import { Query, Mutation, AdminPermission } from '@graphql/types';
import { getAdminPermission } from '@lib/enum';

import Headline from '@components/layout/Headline';
import {
    DetailSection,
    DetailDataRow,
    InfoTitle
} from '@components/share/DetailPage/DetailSection';
import { Contents, FlexBox } from '@components/styled/StyledComponents';
import OperatorModal from '@components/feature/Setting/Sysop/OperatorModal';
import PermissionModal from '@components/feature/Setting/Sysop/PermissionModal';
import Loader from '@components/share/Loader';

export type OperatorDataType = {
    adminId: number | null;
    account: string;
    name: string;
    cellphone: string;
    rank: string;
    memo: string;
};

const SysopDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [openModal, setOpenModal] = useState(false);
    const [permissionModal, setPermissionModal] = useState(false);

    const { data, loading } = useQuery<Pick<Query, 'seeAdminDetail'>>(
        SEE_ADMIN_DETAIL,
        {
            variables: {
                adminId: +(id as string)
            }
        }
    );
    const [initAdminPw] =
        useMutation<Pick<Mutation, 'initAdminPw'>>(INIT_ADMIN_PW);
    const [deleteAdmin] =
        useMutation<Pick<Mutation, 'deleteAdmin'>>(DELETE_ADMIN);

    const {
        adminId,
        createdAt,
        updatedAt,
        name,
        cellphone,
        account,
        rank,
        adminPermission,
        latestLogin,
        memo
    } = data?.seeAdminDetail ?? {};

    const handleResetPw = async () => {
        if (!window.confirm('비밀번호를 초기화 하시겠습니까?')) return;
        try {
            await initAdminPw({
                variables: {
                    adminId
                },
                onCompleted: () =>
                    toast.success('비밀번호가 초기화 되었습니다.')
            });
        } catch (err) {
            console.error(err);
        }
    };

    const onDelete = async () => {
        if (!window.confirm('운영자를 삭제하시겠습니까?')) return;
        try {
            await deleteAdmin({
                variables: {
                    adminId
                },
                onCompleted: () => {
                    toast.success('운영자가 삭제되었습니다.');
                    navigate('/setting/sysop');
                }
            });
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) return <Loader />;

    return (
        <Contents>
            <Headline
                title="운영자 상세 정보"
                subTitle="펫이닷 운영자 상세 정보 조회"
                buttonList={['수정', '삭제']}
                buttonAction={[() => setOpenModal(true), onDelete]}
                DETAIL
            />
            <DetailSection>
                <FlexBox $alignItems="flex-start">
                    <InfoTitle>기본정보</InfoTitle>
                    <FlexBox $flexDirection="column" $alignItems="flex-start">
                        <DetailDataRow
                            title="계정명(이메일)"
                            value={account || '-'}
                            margin="0 0 20px"
                            buttonTitle="비밀번호 초기화"
                            onButtonClick={handleResetPw}
                        />
                        <DetailDataRow
                            title="이름"
                            value={name || '-'}
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="직책"
                            value={rank || '-'}
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="휴대폰 번호"
                            value={cellphone ? autoHyphen(cellphone) : '-'}
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="최근 로그인"
                            value={
                                latestLogin
                                    ? dayjs(latestLogin).format(
                                          'YYYY-MM-DD HH:mm'
                                      )
                                    : '-'
                            }
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="최종 수정일"
                            value={
                                updatedAt
                                    ? dayjs(updatedAt).format(
                                          'YYYY-MM-DD HH:mm'
                                      )
                                    : '-'
                            }
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="최초 생성일"
                            value={
                                createdAt
                                    ? dayjs(createdAt).format(
                                          'YYYY-MM-DD HH:mm'
                                      )
                                    : '-'
                            }
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="관리자 메모"
                            value={memo || '-'}
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="탭별 접근 권한"
                            value={getAdminPermission(
                                adminPermission as AdminPermission
                            )}
                            buttonTitle="권한 수정"
                            onButtonClick={() => setPermissionModal(true)}
                            margin="0 0 20px"
                        />
                    </FlexBox>
                </FlexBox>
            </DetailSection>
            {openModal && (
                <OperatorModal
                    isOpen={openModal}
                    setIsOpen={setOpenModal}
                    data={
                        {
                            adminId,
                            account,
                            name,
                            cellphone,
                            rank,
                            memo
                        } as OperatorDataType
                    }
                />
            )}
            {permissionModal && (
                <PermissionModal
                    isOpen={permissionModal}
                    setIsOpen={setPermissionModal}
                    data={adminPermission as AdminPermission}
                />
            )}
        </Contents>
    );
};

export default SysopDetail;
